import * as React from 'react';

type TUseMobileStickyUntilVisibleParams = {
  targetRef: React.RefObject<Element | null>;
  threshold?: number;
  maxWidthPx?: number;
  dependencies?: React.DependencyList;
};

export const useMobileStickyUntilVisible = ({
  targetRef,
  threshold = 0.2,
  maxWidthPx = 768,
  dependencies = [],
}: TUseMobileStickyUntilVisibleParams): boolean => {
  const [isStickyVisible, setIsStickyVisible] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia(`(max-width: ${maxWidthPx}px)`);
    let observer: IntersectionObserver | null = null;

    const updateVisibility = () => {
      if (!targetRef.current || !mediaQuery.matches) {
        setIsStickyVisible(false);
        if (observer) {
          observer.disconnect();
          observer = null;
        }
        return;
      }

      if (!observer) {
        observer = new IntersectionObserver(
          ([entry]) => {
            setIsStickyVisible(!entry.isIntersecting);
          },
          {
            threshold,
          }
        );
      }

      observer.disconnect();
      observer.observe(targetRef.current);
    };

    updateVisibility();
    mediaQuery.addEventListener('change', updateVisibility);

    return () => {
      mediaQuery.removeEventListener('change', updateVisibility);
      if (observer) {
        observer.disconnect();
      }
    };
  }, [maxWidthPx, targetRef, threshold, ...dependencies]);

  return isStickyVisible;
};
